import { Injectable } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
  CourseLevel,
  SyllabusHttpModel,
  SyllabusUserSubscriptionQuery,
  TopicLevel2HttpModel,
  TopicLevel2Service,
} from '@examdojo/category/v2';
import { DateTimeService } from '@examdojo/core/date-time';
import { ErrorHandlerService } from '@examdojo/error-handling';
import { ExamDateQuery } from '@examdojo/exam-date';
import { distinctUntilArrayChanged, shareOneReplay } from '@examdojo/rxjs';
import { TableInsertModel, TableRowModel, TableUpdateModel } from '@examdojo/supabase';
import { UserQuery } from '@examdojo/user';
import { assertNonNullable } from '@examdojo/util/assert';
import { sanitizeObject } from '@examdojo/util/sanitize-object';
import {
  combineLatest,
  debounceTime,
  distinctUntilChanged,
  filter,
  forkJoin,
  map,
  merge,
  Observable,
  of,
  switchMap,
  tap,
} from 'rxjs';
import { AssessmentHttpService } from './assessment-http.service';
import { AssessmentTL2RelationshipStoreModel } from './assessment-topic-level-2/assessment-tl2-relationship.model';
import { AssessmentTL2RelationshipQuery } from './assessment-topic-level-2/assessment-tl2-relationship.query';
import { AssessmentTl2RelationshipService } from './assessment-topic-level-2/assessment-tl2-relationship.service';
import {
  ASSESSMENT_CREATE_ALLOWED_KEYS,
  ASSESSMENT_UPDATE_ALLOWED_KEYS,
  AssessmentCreateModel,
  AssessmentHttpModel,
  AssessmentStoreModel,
  AssessmentUpdateModel,
  FINAL_EXAM_ASSESSMENT_ID,
} from './assessment.model';
import { AssessmentQuery } from './assessment.query';
import { AssessmentStore } from './assessment.store';

@Injectable({
  providedIn: 'root',
})
export class AssessmentService {
  constructor(
    private readonly store: AssessmentStore,
    private readonly query: AssessmentQuery,
    private readonly httpService: AssessmentHttpService,
    private readonly topicLevel2Service: TopicLevel2Service,
    private readonly assessmentTl2RelationshipService: AssessmentTl2RelationshipService,
    private readonly assessmentTL2RelationshipQuery: AssessmentTL2RelationshipQuery,
    private readonly userQuery: UserQuery,
    private readonly dateTimeService: DateTimeService,
    private readonly examDateQuery: ExamDateQuery,
    private readonly syllabusUserSubscriptionQuery: SyllabusUserSubscriptionQuery,
    private readonly errorHandlerService: ErrorHandlerService,
  ) {
    merge(this.synchronizeActiveEntity(), this.fetchPredictedScoreOnActiveAssessmentChange())
      .pipe(takeUntilDestroyed())
      .subscribe();
  }

  fetchAll(filters?: {
    syllabusId?: SyllabusHttpModel['id'];
    courseLevel?: CourseLevel | null;
  }): Observable<AssessmentStoreModel[]> {
    return forkJoin([this.httpService.fetchAll(filters), this.assessmentTl2RelationshipService.fetchAll()]).pipe(
      this.errorHandlerService.setHttpErrorMetadata({ entity: 'examdojo.entity.assessments' }),
      map(([assessments, assessmentTL2Relationships]) =>
        assessments.map((item) =>
          this.mapHttpModelToStoreModel(
            item,
            this.extractTopicLevel2IdsForAssessment(item.id, assessmentTL2Relationships),
          ),
        ),
      ),
      map((storeModels) => [...storeModels, this.getFinalExamAssessment()]),
      tap((storeModels) => this.store.set(storeModels)),
    );
  }

  update(
    id: AssessmentStoreModel['id'],
    model: AssessmentUpdateModel,
    topicLevel2Ids: Array<AssessmentTL2RelationshipStoreModel['topic_level_02_id']> = [],
  ) {
    const assessmentBeforeUpdate = this.query.getUIEntity(id);
    assertNonNullable(assessmentBeforeUpdate, 'assessmentBeforeUpdate');

    const updateModel = this.mapUpdateModelToHttpModel({
      ...model,
      active: undefined, // let the setActive method handle this
    });

    const assessmentUpdate$ = this.httpService.update(id, updateModel);
    const tl2RelationshipsUpdate$ = this.assessmentTl2RelationshipService.setMany(id, topicLevel2Ids);

    return combineLatest([assessmentUpdate$, tl2RelationshipsUpdate$]).pipe(
      this.errorHandlerService.setHttpErrorMetadata({ entity: 'examdojo.entity.assessments', action: 'update' }),
      map(([httpModel]) => {
        const assessmentTL2Relationships = this.assessmentTL2RelationshipQuery.getAllUIEntities();
        return this.mapHttpModelToStoreModel(
          httpModel,
          this.extractTopicLevel2IdsForAssessment(httpModel.id, assessmentTL2Relationships),
        );
      }),
      tap((storeModel) => {
        this.store.upsert(id, storeModel);
      }),
      switchMap(() => {
        const hasActiveValueChanged = assessmentBeforeUpdate.active !== model.active;
        return hasActiveValueChanged && model.active ? this.setActive(id) : of();
      }),
    );
  }

  create(
    model: AssessmentCreateModel,
    topicLevel2Ids: Array<AssessmentTL2RelationshipStoreModel['topic_level_02_id']> = [],
  ) {
    const createModel = this.mapCreateModelToHttpModel({
      ...model,
      active: undefined, // let the setActive method handle this
    });

    return this.httpService.create(createModel).pipe(
      this.errorHandlerService.setHttpErrorMetadata({ entity: 'examdojo.entity.assessments', action: 'create' }),
      switchMap((httpModel) => {
        const tl2RelationshipsUpdate$ = topicLevel2Ids.length
          ? this.assessmentTl2RelationshipService.setMany(
              httpModel.id,
              topicLevel2Ids.map((value: string | number) => Number(value)),
            )
          : of();

        return tl2RelationshipsUpdate$.pipe(map(() => httpModel));
      }),

      map((httpModel) => {
        const assessmentTL2Relationships = this.assessmentTL2RelationshipQuery.getAllUIEntities();
        return this.mapHttpModelToStoreModel(
          httpModel,
          this.extractTopicLevel2IdsForAssessment(httpModel.id, assessmentTL2Relationships),
        );
      }),
      tap((storeModel) => {
        this.store.upsert(storeModel.id, storeModel);
      }),
      switchMap((storeModel) => (model.active ? this.setActive(storeModel.id) : of())),
    );
  }

  delete(id: AssessmentStoreModel['id']) {
    return this.httpService.delete(id).pipe(
      tap(() => {
        this.store.delete(id);
      }),
      this.errorHandlerService.setHttpErrorMetadata({ entity: 'examdojo.entity.assessments', action: 'delete' }),
    );
  }

  setActive(id: AssessmentStoreModel['id']): Observable<void> {
    return this.httpService.setActive(id).pipe(
      this.errorHandlerService.setHttpErrorMetadata({ entity: 'examdojo.entity.assessments', action: 'update' }),
      tap(() => {
        this.store.updateEntitiesByPredicate(
          () => true,
          (entity) => ({ ...entity, active: entity.id === id }),
        );
        this.store.setActive(id);
      }),
    );
  }

  private mapCreateModelToHttpModel(model: AssessmentCreateModel): TableInsertModel<'assessments', 'learning'> {
    return {
      ...sanitizeObject(model, ASSESSMENT_CREATE_ALLOWED_KEYS),
      user_id: undefined as unknown as string, // Will be set automatically by database trigger
    };
  }

  private mapUpdateModelToHttpModel(model: AssessmentUpdateModel): TableUpdateModel<'assessments', 'learning'> {
    return sanitizeObject(model, ASSESSMENT_UPDATE_ALLOWED_KEYS);
  }

  private mapHttpModelToStoreModel(
    httpModel: AssessmentHttpModel,
    topicLevel2Ids: Array<TopicLevel2HttpModel['id']>,
  ): AssessmentStoreModel {
    const targetDate = this.dateTimeService.parseDate(httpModel.target_date);
    return { ...httpModel, topicLevel2Ids, target_date: targetDate };
  }

  private synchronizeActiveEntity() {
    const activeEntityId$ = this.query.selectAllEntities().pipe(
      debounceTime(0),
      filter((entities) => entities.length > 0),
      map((entities) => entities.find((model) => model.active)?.id ?? FINAL_EXAM_ASSESSMENT_ID),
      distinctUntilChanged((a, b) => {
        if (a === FINAL_EXAM_ASSESSMENT_ID && b === FINAL_EXAM_ASSESSMENT_ID) {
          // Since the final exam id is shared for all courses, we want to make sure the active entity is synchronized even when the course changed
          return false;
        }
        return a === b;
      }),
      shareOneReplay(),
    );

    const setActiveEntity$ = activeEntityId$.pipe(tap((activeId) => this.store.setActive(activeId)));

    const makeFinalAssessmentActive$ = activeEntityId$.pipe(
      map((id) => id === FINAL_EXAM_ASSESSMENT_ID),
      tap((isFinalAssessmentActive) => {
        const finalExamAssessment = this.query.getEntity(FINAL_EXAM_ASSESSMENT_ID);
        if (!finalExamAssessment) {
          return;
        }

        if (finalExamAssessment.active !== isFinalAssessmentActive) {
          this.store.update(FINAL_EXAM_ASSESSMENT_ID, { active: isFinalAssessmentActive });
        }
      }),
    );

    return merge(setActiveEntity$, makeFinalAssessmentActive$);
  }

  private fetchPredictedScoreOnActiveAssessmentChange() {
    return this.query.active$.pipe(
      map((activeAssessment) => activeAssessment?.topicLevel2Ids ?? []),
      distinctUntilArrayChanged({ orderMatters: false }),
      switchMap((tl2Ids) => this.topicLevel2Service.fetchPredictedScore(tl2Ids)),
    );
  }

  private extractTopicLevel2IdsForAssessment(
    assessmentId: AssessmentStoreModel['id'],
    assessmentsTL2: Array<TableRowModel<'assessments_topics_level_02', 'learning'>>,
  ) {
    return assessmentsTL2.filter((tl2) => tl2.assessment_id === assessmentId).map((tl2) => tl2.topic_level_02_id);
  }

  private getFinalExamAssessment(): AssessmentStoreModel {
    const user = this.userQuery.getActive()!;
    const examDateId = this.syllabusUserSubscriptionQuery.getActiveCourseExamDateId();
    const examDate = examDateId ? this.examDateQuery.getUIEntity(examDateId) : null;

    return {
      id: FINAL_EXAM_ASSESSMENT_ID,
      created_at: new Date().toISOString(),
      user_id: user.id,
      title: 'The Final Exam',
      target_date: examDate ? examDate.inferredDate : new Date(), // will be updated in the UI model
      assessment_type: 'FINAL_EXAM',
      active: false,
      grade_percentage: null,
      topicLevel2Ids: [],
      syllabus_id: user.syllabus_id ?? 0,
    };
  }
}
